import type {
  ChangeRequestAction,
  CodeListMap,
  DataValue,
} from 'schemaDefinition/types';
import type {
  ChangeRequest,
  EditMode,
  ExpressionV4,
  ManifestationStatus,
  ManifestationV4,
  Metadata,
  MetadataEditState,
  MetadataSchemas,
  WorkV4,
} from './types';

export type MetadataEditAction_Reset = {
  type: 'METADATAEDIT_RESET';
};

export type MetadataEditAction_Loaded = {
  /** Metadata has been loaded from backend */
  type: 'METADATAEDIT_LOADED';
  payload: {
    schemas: MetadataSchemas;
    codelists: CodeListMap;
    metadata: Metadata;
  };
};

export type MetadataEditAction_Cancelled = {
  type: 'METADATAEDIT_CANCELLED';
  payload?: undefined;
};

export type MetadataEditAction_Saved = {
  type: 'METADATAEDIT_SAVED';
  payload?: undefined;
};

export type MetadataEditAction_WorkEdited = {
  type: 'METADATAEDIT_WORK_EDITED';
  payload: WorkV4;
};

export type MetadataEditAction_WorkSaved = {
  type: 'METADATAEDIT_WORK_SAVED';
  payload: WorkV4;
};

export type MetadataEditAction_ExpressionEdited = {
  type: 'METADATAEDIT_EXPRESSION_EDITED' | 'METADATAEDIT_NEW_EXPRESSION_EDITED';
  payload: ExpressionV4;
};

export type MetadataEditAction_ExpressionSaved = {
  type: 'METADATAEDIT_EXPRESSION_SAVED';
  payload: ExpressionV4;
};

export type MetadataEditAction_ManifestationEdited = {
  type: 'METADATAEDIT_MANIFESTATION_EDITED';
  payload: ManifestationV4;
};

export type MetadataEditAction_ManifestationSaved = {
  type: 'METADATAEDIT_MANIFESTATION_SAVED';
  payload: {
    manifestation: ManifestationV4;
    work: WorkV4;
  };
};

export type MetadataEditAction_NewExpressionWithManifestationSaved = {
  type: 'METADATAEDIT_NEW_EXPRESSION_WITH_MANIFESTATION_SAVED';
  payload: {
    localExpressionId: string;
    expression: ExpressionV4;
    manifestation: ManifestationV4;
  };
};

export type MetadataEditAction_ChangeRequestEdited = {
  type: 'METADATAEDIT_CHANGEREQUEST_EDITED';
  payload: ChangeRequest;
};

export type MetadataEditAction_ApproveChangeRequestItem = {
  type: 'METADATAEDIT_APPROVE_CHANGEREQUESTITEM';
  payload: {
    changeRequestId: string;
    /** Root path of value, including 'work', 'expression' or 'manifestation' */
    rootValuePath: string;
    itemAction: ChangeRequestAction;
    /**
     * Use given value to set value at valuePath, independent action specified
     * If not provided, change request value at valuePath will be used
     */
    newValue?: DataValue;

    /**
     * "Path" to review status, default same as valuePath. Should be provided when
     * value comparison doesn't match specific values in original/change request data,
     * e.g. when comparing thesaurus terms grouped by category
     */
    statusPath?: string;
  };
};

/** Close change request, remaing items are rejected (implicitly) */
export type MetadataEditAction_NewChangeRequestFromCopy = {
  type: 'METADATAEDIT_NEW_CHANGEREQUEST_FROM_COPY';
  payload: ChangeRequest;
};

export type MetadataEditAction_CloseChangeRequest = {
  type: 'METADATAEDIT_CLOSE_CHANGEREQUEST';
  payload: {
    changeRequestId: string;
  };
};

export type MetadataEditAction_ChangeRequestSaved = {
  type: 'METADATAEDIT_CHANGEREQUEST_SAVED';
  payload: ChangeRequest;
};

export type MetadataEditAction_ManifestationStatusPreview = {
  type: 'METADATAEDIT_MANIFESTATION_STAUTS_PREVIEW';
  payload: {
    id: string;
    status?: ManifestationStatus;
  };
};

export type MetadataEditAction_ManifestationStatusChanged = {
  type: 'METADATAEDIT_MANIFESTATION_STATUS_CHANGED';
  payload: {
    id: string;
    status?: ManifestationStatus;
  };
};

export type MetadataEditAction_MoveManifestationToOtherExpression = {
  type: 'MOVE_MANIFESTATION_TO_OTHER_EXPRESSION';
  payload: {
    manifestationId: string;
    expressionId: string;
  };
};

export type MetadataEditAction_MoveManifestationToNewExpression = {
  type: 'MOVE_MANIFESTATION_TO_NEW_EXPRESSION';
  payload: {
    manifestationId: string;
  };
};

export type MetadataEditAction_CancelMoveManifestationToNewExpression = {
  type: 'CANCEL_MOVE_MANIFESTATION_TO_NEW_EXPRESSION';
  payload: {
    manifestationId: string;
  };
};

export type MetadataEditAction_SetEditMode = {
  type: 'METADATAEDIT_SET_EDITMODE';
  payload: {
    id: string;
    key: string;
    mode: EditMode;
  };
};

export type MetadataEditAction_SetSaving = {
  type: 'METADATAEDIT_SET_SAVING';
  payload: {
    saving: boolean;
  };
};

export type MetadataEditAction =
  | MetadataEditAction_Reset
  | MetadataEditAction_Loaded
  | MetadataEditAction_Cancelled
  | MetadataEditAction_Saved
  | MetadataEditAction_WorkEdited
  | MetadataEditAction_WorkSaved
  | MetadataEditAction_ExpressionEdited
  | MetadataEditAction_ExpressionSaved
  | MetadataEditAction_NewExpressionWithManifestationSaved
  | MetadataEditAction_ManifestationEdited
  | MetadataEditAction_ManifestationSaved
  | MetadataEditAction_ChangeRequestEdited
  | MetadataEditAction_ApproveChangeRequestItem
  | MetadataEditAction_NewChangeRequestFromCopy
  | MetadataEditAction_CloseChangeRequest
  | MetadataEditAction_ChangeRequestSaved
  | MetadataEditAction_ManifestationStatusPreview
  | MetadataEditAction_ManifestationStatusChanged
  | MetadataEditAction_MoveManifestationToOtherExpression
  | MetadataEditAction_MoveManifestationToNewExpression
  | MetadataEditAction_CancelMoveManifestationToNewExpression
  | MetadataEditAction_SetEditMode
  | MetadataEditAction_SetSaving;

export const MetadataEditDefaultState: MetadataEditState = {
  saving: false,
  metadata: undefined,
  approvedChanges: {},
  timestamps: {},
  newExpressionIds: [],
  order: [],
  statuses: {},
  editStatuses: {},
  editModes: {},
  exports: {
    work: {
      type: 'BOOK',
    },
    expressions: {},
    manifestations: {},
  },
};
